import React, { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getRobotExtraInfoType, RobotExtraInfo } from "./RobotExtraInfo";

const FILE_SERVER =
  process.env.REACT_APP_FILE_SERVER || "https://servisas.gpsmeistras.lt";

const STATUS_STEPS = [
  {
    key: "Užregistruota",
    title: "Užregistruota",
    description: "Užklausa gauta sistemoje.",
  },
  {
    key: "Prekė gauta į servisą",
    title: "Gauta į servisą",
    description: "Įrenginys pasiekė servisą.",
  },
  {
    key: "Diagnostika",
    title: "Diagnostika",
    description: "Tikriname gedimą.",
  },
  {
    key: "Remontuojama",
    title: "Remontuojama",
    description: "Atliekami remonto darbai.",
  },
  {
    key: "Paruošta atsiėmimui",
    title: "Paruošta",
    description: "Įrenginys paruoštas atsiėmimui.",
  },
  {
    key: "Uždaryta",
    title: "Baigta",
    description: "Serviso procesas užbaigtas.",
  },
];

const STATUS_ALIASES = {
  "Laukiama prekė iš kliento": "Užregistruota",
  "Laukiama papildoma informacija iš kliento": "Diagnostika",
  "Išsiųsta į autorizuotą servisą": "Remontuojama",
  "Dalių užsakymas": "Remontuojama",
  "Prekė išsiųsta klientui": "Uždaryta",
};

function StatusTimeline({ currentStatus }) {
  const normalizedStatus = STATUS_ALIASES[currentStatus] || currentStatus;
  const currentIndex = STATUS_STEPS.findIndex(
    (step) => step.key === normalizedStatus
  );

  return (
    <div className="bg-white border rounded-2xl p-5 md:p-6 mb-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Serviso eiga</h3>
          <p className="text-sm text-gray-500">
            Dabartinė būsena:{" "}
            <span className="font-semibold text-blue-700">
              {currentStatus}
            </span>
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-6">
        {STATUS_STEPS.map((step, index) => {
          const isDone = currentIndex >= index;
          const isCurrent = currentIndex === index;

          return (
            <div key={step.key} className="relative">
              <div
                className={`rounded-xl border p-4 h-full ${
                  isCurrent
                    ? "border-blue-600 bg-blue-50"
                    : isDone
                    ? "border-green-500 bg-green-50"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center font-bold mb-3 ${
                    isCurrent
                      ? "bg-blue-600 text-white"
                      : isDone
                      ? "bg-green-600 text-white"
                      : "bg-gray-300 text-gray-700"
                  }`}
                >
                  {isDone ? "✓" : index + 1}
                </div>

                <div className="font-semibold text-gray-900">
                  {step.title}
                </div>

                <div className="text-xs text-gray-500 mt-1 leading-5">
                  {step.description}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}  


const RmaStatusCheck = () => {
  const { rma: rmaFromUrl } = useParams();
  const [rma, setRma] = useState(rmaFromUrl || "");
  const [phoneLast4, setPhoneLast4] = useState("");
  const [statusInfo, setStatusInfo] = useState(null);
  const [error, setError] = useState("");

  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const handleCheck = async (e) => {
    e.preventDefault();

    setStatusInfo(null);
    setError("");

    if (!rma.trim()) {
      setError("Įveskite RMA kodą.");
      return;
    }

    if (!phoneLast4.trim() || phoneLast4.trim().length !== 4) {
      setError("Įveskite paskutinius 4 telefono numerio skaitmenis.");
      return;
    }

    try {
      const res = await axios.post(`/api/rma/status/${rma.trim()}`, {
        phoneLast4: phoneLast4.trim(),
      });

      setStatusInfo(res.data);
    } catch (err) {
      setError("RMA kodas nerastas arba neteisingi telefono skaitmenys.");
    }
  };

  const refreshStatus = async () => {
    const refreshed = await axios.post(`/api/rma/status/${statusInfo.rmaCode}`, {
      phoneLast4: phoneLast4.trim(),
    });

    setStatusInfo(refreshed.data);
  };

  const handlePasteFiles = (e) => {
    const pastedFiles = Array.from(e.clipboardData.files || []);

    if (pastedFiles.length > 0) {
      setFiles((prev) => [...prev, ...pastedFiles]);
    }
  };

  const removeSelectedFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const renderMessageText = (text = "") => {
    const parts = text.split(/(\*\*.*?\*\*)/g);

    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }

      return <span key={index}>{part}</span>;
    });
  };

  const handleSendMessage = async () => {
    if (!message.trim() && files.length === 0) {
      return;
    }

    try {
      setSending(true);

      const formData = new FormData();

      formData.append("message", message);
      formData.append("phoneLast4", phoneLast4.trim());

      files.forEach((file) => {
        formData.append("files", file);
      });

      await axios.post(
        `/api/rma/status/${statusInfo.rmaCode}/message`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      await refreshStatus();

      setMessage("");
      setFiles([]);
    } catch (err) {
      console.error(err);
      alert("Nepavyko išsiųsti žinutės");
    } finally {
      setSending(false);
    }
  };

  const sortedMessages = [...(statusInfo?.messages || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8">
      <div className="w-full max-w-5xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-blue-700">
            GPSmeistras Servisas
          </h1>
          <p className="text-gray-600 mt-1">
            RMA būsenos patikra ir susirašinėjimas su servisu
          </p>
        </div>

        <div className="bg-white p-6 md:p-8 rounded-2xl shadow">
        <h2 className="text-2xl font-bold mb-6 text-center">
          🔍 RMA statuso patikra
        </h2>

        <form
          onSubmit={handleCheck}
          className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-2 mb-6"
        >
          <input
            type="text"
            placeholder="Įveskite RMA kodą"
            value={rma}
            onChange={(e) => setRma(e.target.value)}
            className="border rounded-md p-2"
          />

          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            placeholder="Paskutiniai 4 tel. skaitmenys"
            value={phoneLast4}
            onChange={(e) =>
              setPhoneLast4(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
            className="border rounded-md p-2"
          />

          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Tikrinti
          </button>
        </form>

        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
            {error}
          </div>
        )}

        {statusInfo && (
          <>
            <div className="bg-gray-50 p-4 rounded-md border mb-6">
              <p>
                <strong>RMA kodas:</strong> {statusInfo.rmaCode}
              </p>

              <p>
                <strong>Klientas:</strong> {statusInfo.clientName}
              </p>

              <p>
                <strong>Būsena:</strong> {statusInfo.status}
              </p>

              <p>
                <strong>Užregistruota:</strong>{" "}
                {new Date(statusInfo.receivedDate).toLocaleDateString("lt-LT")}
              </p>
            </div>

              {statusInfo?.messages?.length === 0 && (
                <RobotExtraInfo
                  rmaCode={statusInfo.rmaCode}
                  extraInfoType={getRobotExtraInfoType(
                    statusInfo.product?.category,
                    statusInfo.product?.brand,
                    statusInfo.product?.model
                  )}
                />
              )}

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-blue-600 text-white px-4 py-3 font-semibold">
                💬 Susirašinėjimas
              </div>

              <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto bg-gray-50">
                {sortedMessages.length === 0 && (
                  <p className="text-gray-500 text-sm">
                    Susirašinėjimo dar nėra.
                  </p>
                )}

                {sortedMessages.map((msg, index) => {
                  const isClient = msg.sender === "client";

                  return (
                    <div
                      key={index}
                      className={`flex ${
                        isClient ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[85%] rounded-xl px-4 py-3 shadow text-sm ${
                          isClient
                            ? "bg-blue-600 text-white"
                            : "bg-white border text-gray-900"
                        }`}
                      >
                        <div className="font-semibold mb-1">
                          {isClient ? "Jūs" : "Servisas"}
                        </div>

                        {msg.message && (
                          <div className="whitespace-pre-wrap">
                            {renderMessageText(msg.message)}
                          </div>
                        )}

                        {msg.attachments?.length > 0 && (
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            {msg.attachments.map((file, i) => {
                              const url = `${FILE_SERVER}/uploads/${file}`;
                              const isImage =
                                file.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                              const isVideo =
                                file.match(/\.(mp4|webm|mov|avi)$/i);

                              return (
                                <div key={i}>
                                  {isImage && (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <img
                                        src={url}
                                        alt=""
                                        className="rounded max-h-48 object-cover border"
                                      />
                                    </a>
                                  )}

                                  {isVideo && (
                                    <video
                                      src={url}
                                      controls
                                      className="rounded max-h-48 border"
                                    />
                                  )}

                                  {!isImage && !isVideo && (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="underline"
                                    >
                                      Atsisiųsti failą
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div
                          className={`text-xs mt-2 ${
                            isClient ? "text-blue-100" : "text-gray-500"
                          }`}
                        >
                          {new Date(msg.createdAt).toLocaleString("lt-LT")}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t bg-white p-4 space-y-3">
                <textarea
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onPaste={handlePasteFiles}
                  placeholder="Įrašykite žinutę servisui... Galite įklijuoti nuotrauką. Paryškinimui naudokite **tekstą**."
                  className="w-full border rounded-md p-3"
                />

                <input
                  type="file"
                  multiple
                  accept="image/*,video/*,application/pdf"
                  onChange={(e) =>
                    setFiles((prev) => [...prev, ...Array.from(e.target.files)])
                  }
                  className="block w-full"
                />

                {files.length > 0 && (
                  <div className="bg-gray-50 border rounded-md p-3 text-sm text-gray-700 space-y-2">
                    <div className="font-medium">
                      Pridėta failų: {files.length}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {files.map((file, index) => (
                        <button
                          key={`${file.name}-${index}`}
                          type="button"
                          onClick={() => removeSelectedFile(index)}
                          className="bg-white border px-3 py-1 rounded hover:bg-red-50"
                        >
                          {file.name} ✕
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={sending || (!message.trim() && files.length === 0)}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-5 py-2 rounded-md w-full"
                >
                  {sending ? "Siunčiama..." : "📤 Siųsti žinutę"}
                </button>
              </div>
            </div>

            <StatusTimeline currentStatus={statusInfo.status} />

          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default RmaStatusCheck;